
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/context/UserContext';
import Layout from '@/components/Layout';
import { useSymptomData } from '@/hooks/useSymptomData';
import { useLessonCalendar } from '@/hooks/useLessonCalendar';
import { useCycleData } from '@/hooks/useCycleData';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import DashboardTabs from '@/components/dashboard/DashboardTabs';
import OverviewTab from '@/components/dashboard/OverviewTab';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLeaderboardData } from '@/components/leaderboard/useLeaderboardData';
import LeaderboardTable from '@/components/leaderboard/LeaderboardTable';
import UserRankCard from '@/components/leaderboard/UserRankCard';
import ZoneFilter from '@/components/leaderboard/ZoneFilter';
import LeaderboardTips from '@/components/leaderboard/LeaderboardTips';
import { FadeIn } from '@/components/Animations';

const IntegratedDashboard = () => {
  const { user, isAuthenticated, isLoading, userProgress } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const isMobile = useIsMobile();
  const { symptomData, setCycleData: updateSymptomCycleData } = useSymptomData(user?.id);
  
  // Custom hooks for different dashboard features
  const { selectedDate, selectedDay, currentLesson, handleDateSelect } = useLessonCalendar(userProgress.currentDay);
  const { cycleData } = useCycleData(updateSymptomCycleData);
  
  // Get leaderboard data
  const {
    leaderboardData,
    selectedZone,
    setSelectedZone,
    getSortedData,
    getUserRank
  } = useLeaderboardData(user, userProgress);
  
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);
  
  if (isLoading) {
    return (
      <Layout>
        <div className="container py-8">
          <p className="text-center">Loading...</p>
        </div>
      </Layout>
    );
  }
  
  if (!user) return null;
  
  // Generate page title based on active tab
  const getPageTitle = () => {
    switch (activeTab) {
      case 'overview': return 'Health Dashboard';
      case 'leaderboard': return 'Hormony Leaderboard';
      default: return 'Health Dashboard';
    }
  };
  
  const getPageDescription = () => {
    switch (activeTab) {
      case 'overview': return 'Your personalized health insights and progress overview';
      case 'leaderboard': return 'Top achievers and community rankings';
      default: return 'Your hormone health companion';
    }
  };
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'leaderboard') {
      // Just switch the tab, don't navigate away
    }
  };
  
  // Find the current user's rank
  const currentUserRank = getUserRank();
  const sortedData = getSortedData();
  
  // Find the user entry in the leaderboard data to access zones
  const userLeaderboardEntry = leaderboardData.find(entry => entry.id === user.id);
  
  return (
    <Layout>
      <div className="container py-4 max-w-5xl mx-auto px-3 md:px-4">
        {/* Welcome message section */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-hormony-charcoal mb-2">
            Welcome, {user.nickname || user.name?.split(' ')[0] || 'Friend'}!
          </h1>
          <div className="w-full h-[1px] bg-gray-200 my-4"></div>
        </div>
        
        <DashboardHeader 
          title={getPageTitle()} 
          description={getPageDescription()}
        />
        
        <DashboardTabs activeTab={activeTab} onTabChange={handleTabChange}>
          {/* Overview Tab */}
          <TabsContent value="overview">
            <OverviewTab 
              selectedDate={selectedDate}
              handleDateSelect={handleDateSelect}
              cycleData={cycleData}
              currentLesson={currentLesson}
              selectedDay={selectedDay}
              userProgress={userProgress}
            />
          </TabsContent>
          
          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <FadeIn>
              <UserRankCard 
                user={user}
                userProgress={userProgress}
                currentUserRank={currentUserRank}
                selectedZone={selectedZone}
                userLeaderboardEntry={userLeaderboardEntry}
              />
              
              <ZoneFilter 
                selectedZone={selectedZone}
                setSelectedZone={setSelectedZone}
              />
              
              <LeaderboardTable 
                sortedData={sortedData}
                selectedZone={selectedZone}
                currentUser={user}
              />
              
              <LeaderboardTips />
            </FadeIn>
          </TabsContent>
        </DashboardTabs>
      </div>
    </Layout>
  );
};

export default IntegratedDashboard;
